import { createFeatureSelector, createSelector } from '@ngrx/store';
import { List } from 'immutable';
import { combineActions, handleActions } from 'redux-actions';
import {
  ActionsEnum,
  AddAccountUser,
  AddAccountUserNetworkSuccess,
  DeleteAccountUser,
  FetchAccountUsersError,
  UpdateAccountUserNetworkSuccess,
  UpdateAccountUsers,
} from './account-user-data.actions';
import { AccountUser } from './account-user.model';

// feature name
export const FEATURE_NAME = 'account-user-data';

// state interface
export interface IState {
  accountUsers: List<AccountUser>;
  fetchError: Error;
}

// initial state
export const initialState: IState = {
  accountUsers: null,
  fetchError: null,
};

// reducer
const updateAccountUserInList = (
  state: IState,
  accountUser: AccountUser
): IState => {
  const currentList: List<AccountUser> = state.accountUsers || List([]);
  const index: number = currentList.findIndex(
    (aUser: AccountUser) =>
      aUser.userId === accountUser.userId || aUser.email === accountUser.email
  );

  if (index > -1) {
    return {
      ...state,
      accountUsers: currentList.set(index, accountUser),
    };
  }

  return state;
};

const reducerFn = handleActions(
  {
    [combineActions(
      ActionsEnum.ADD_ACCOUNT_USER_NETWORK_SUCCESS,
      ActionsEnum.UPDATE_ACCOUNT_USER_NETWORK_SUCCESS
    )]: function (
      state: IState,
      {
        payload,
      }: AddAccountUserNetworkSuccess | UpdateAccountUserNetworkSuccess
    ): IState {
      return updateAccountUserInList(state, payload.accountUser);
    },

    [ActionsEnum.FETCH_ACCOUNT_USERS_SUCCESS]: function (
      state: IState,
      { payload }: FetchAccountUsersError
    ): IState {
      return {
        ...state,
        fetchError: null,
      };
    },

    [ActionsEnum.FETCH_ACCOUNT_USERS_ERROR]: function (
      state: IState,
      { payload }: FetchAccountUsersError
    ): IState {
      return {
        ...state,
        fetchError: payload.error,
        accountUsers: !state.accountUsers ? null : state.accountUsers,
      };
    },

    [ActionsEnum.UPDATE_ACCOUNT_USERS]: function (
      state: IState,
      { payload }: UpdateAccountUsers
    ): IState {
      return {
        ...state,
        accountUsers: payload.accountUsers,
      };
    },

    [ActionsEnum.DELETE_ACCOUNT_USER]: function (
      state: IState,
      { payload }: DeleteAccountUser
    ): IState {
      const currentList = state.accountUsers || List([]);
      const index = currentList.findIndex(
        (aU: AccountUser) => aU.userId === payload.accountUser.userId
      );

      if (index > -1) {
        return {
          ...state,
          accountUsers: currentList.delete(index),
        };
      }

      return {
        ...state,
      };
    },

    [ActionsEnum.ADD_ACCOUNT_USER]: function (
      state: IState,
      { payload }: AddAccountUser
    ): IState {
      const currentList: List<AccountUser> = state.accountUsers || List([]);
      const accountUserId = payload.accountUser.userId;
      const index: number = currentList.findIndex(
        (aUser: AccountUser) => aUser.userId === accountUserId
      );

      if (index === -1) {
        return {
          ...state,
          accountUsers: currentList.push(payload.accountUser),
        };
      }

      return {
        ...state,
      };
    },
  } as any,
  initialState
);

// required for AOT
export function reducer(state: IState = initialState, action: any) {
  return reducerFn(state, action);
}

// selectors
const selectFeature = createFeatureSelector<IState>(FEATURE_NAME);

export const getAccountUsers = createSelector(
  selectFeature,
  (state: IState) => state.accountUsers
);
